import React, { createContext, useContext, useReducer, useEffect, useRef } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import APIService from '../services/api';
import rateLimiter from '../utils/rateLimiter';
import { useAuth } from './AuthContext';

// Interfaces para carrito persistente
export interface PersistentCartItem {
  id?: number; // ID del carrito en el servidor
  id_producto: number;
  producto_nombre: string;
  cantidad: number;
  precio_unitario: number;
  subtotal: number;
  imagen?: string;
  stock?: number;
  notas?: string;
  updated_at?: string;
}

export interface PersistentCartState {
  items: PersistentCartItem[];
  subtotal: number;
  itemsCount: number;
  isLoading: boolean;
  isOnline: boolean;
}

// Actions
type PersistentCartAction =
  | { type: 'LOADING' }
  | { type: 'STOP_LOADING' }
  | { type: 'SET_ONLINE'; payload: boolean }
  | { type: 'LOAD_CART'; payload: { items: PersistentCartItem[]; subtotal: number; itemsCount: number } }
  | { type: 'ADD_ITEM'; payload: PersistentCartItem }
  | { type: 'UPDATE_ITEM'; payload: { id_producto: number; cantidad: number } }
  | { type: 'REMOVE_ITEM'; payload: number }
  | { type: 'CLEAR_CART' }
  | { type: 'SYNC_SUCCESS'; payload: { items: PersistentCartItem[]; subtotal: number; itemsCount: number } };

// Reducer
const persistentCartReducer = (state: PersistentCartState, action: PersistentCartAction): PersistentCartState => {
  switch (action.type) {
    case 'LOADING':
      return { ...state, isLoading: true };
    
    case 'STOP_LOADING':
      return { ...state, isLoading: false };
    
    case 'SET_ONLINE':
      return { ...state, isOnline: action.payload };
    
    case 'LOAD_CART':
      return {
        ...state,
        items: action.payload.items,
        subtotal: action.payload.subtotal,
        itemsCount: action.payload.itemsCount,
        isLoading: false
      };
    
    case 'ADD_ITEM': {
      const existingItemIndex = state.items.findIndex(
        item => item.id_producto === action.payload.id_producto
      );
      
      let newItems: PersistentCartItem[];
      if (existingItemIndex >= 0) {
        // Actualizar cantidad del item existente
        newItems = state.items.map((item, index) => 
          index === existingItemIndex 
            ? { 
                ...item, 
                cantidad: Number(item.cantidad) + Number(action.payload.cantidad),
                subtotal: Number(item.precio_unitario) * (Number(item.cantidad) + Number(action.payload.cantidad))
              }
            : item
        );
      } else {
        // Agregar nuevo item
        newItems = [...state.items, action.payload];
      }
      
      const newSubtotal = newItems.reduce((sum, item) => sum + Number(item.subtotal), 0);
      const newItemsCount = newItems.reduce((sum, item) => sum + Number(item.cantidad), 0);
      
      return {
        ...state,
        items: newItems,
        subtotal: newSubtotal,
        itemsCount: newItemsCount,
        isLoading: false
      };
    }
    
    case 'UPDATE_ITEM': {
      const newItems = state.items.map(item =>
        item.id_producto === action.payload.id_producto
          ? { 
              ...item, 
              cantidad: Number(action.payload.cantidad),
              subtotal: Number(item.precio_unitario) * Number(action.payload.cantidad)
            }
          : item
      );
      
      const newSubtotal = newItems.reduce((sum, item) => sum + Number(item.subtotal), 0);
      const newItemsCount = newItems.reduce((sum, item) => sum + Number(item.cantidad), 0);
      
      return {
        ...state,
        items: newItems,
        subtotal: newSubtotal,
        itemsCount: newItemsCount,
        isLoading: false
      };
    }
    
    case 'REMOVE_ITEM': {
      const newItems = state.items.filter(item => item.id_producto !== action.payload);
      const newSubtotal = newItems.reduce((sum, item) => sum + Number(item.subtotal), 0);
      const newItemsCount = newItems.reduce((sum, item) => sum + Number(item.cantidad), 0);
      
      return {
        ...state,
        items: newItems,
        subtotal: newSubtotal,
        itemsCount: newItemsCount,
        isLoading: false
      };
    }
    
    case 'CLEAR_CART':
      return {
        ...state,
        items: [],
        subtotal: 0,
        itemsCount: 0,
        isLoading: false
      };
    
    case 'SYNC_SUCCESS':
      return {
        ...state,
        items: action.payload.items,
        subtotal: action.payload.subtotal,
        itemsCount: action.payload.itemsCount,
        isLoading: false
      };
    
    default:
      return state;
  }
};

// Context
interface PersistentCartContextType {
  state: PersistentCartState;
  addToCart: (product: { id: number; nombre: string; precio: number; imagen?: string; stock?: number }, quantity?: number, notas?: string) => Promise<void>;
  updateQuantity: (productId: number, quantity: number) => Promise<void>;
  removeFromCart: (productId: number) => Promise<void>;
  clearCart: () => Promise<void>;
  loadCart: () => Promise<void>;
  syncCart: () => Promise<void | null>;
}

const PersistentCartContext = createContext<PersistentCartContextType | undefined>(undefined);

// Provider
export const PersistentCartProvider = ({ children }: { children: React.ReactNode }): JSX.Element => {
  const [state, dispatch] = useReducer(persistentCartReducer, {
    items: [],
    subtotal: 0,
    itemsCount: 0,
    isLoading: false,
    isOnline: true
  });
  
  const { user } = useAuth();
  const prevUserIdRef = useRef<number | null>(null);

  // Cargar carrito al inicializar o cuando cambie el usuario
  useEffect(() => {
    const currentUserId = user?.id || null;
    
    // Si cambió el usuario, limpiar carrito y cargar el del nuevo usuario
    if (prevUserIdRef.current !== currentUserId) {
      console.log('🛒 PersistentCartContext: Usuario cambió, limpiando carrito');
      dispatch({ type: 'CLEAR_CART' });
      prevUserIdRef.current = currentUserId;
    }
    
    if (currentUserId) {
      loadCart();
    } else {
      // Si no hay usuario, limpiar el carrito
      dispatch({ type: 'CLEAR_CART' });
    }
  }, [user?.id]);

  // Guardar en AsyncStorage cuando cambie el carrito
  useEffect(() => {
    if (user?.id && (state.items.length > 0 || state.subtotal > 0)) {
      saveCartLocally();
    }
  }, [state.items, state.subtotal, user?.id]);

  const getCartKey = () => {
    // Carrito específico por usuario
    return user?.id ? `persistent_cart_user_${user.id}` : 'persistent_cart_guest';
  };

  const saveCartLocally = async () => {
    try {
      const cartKey = getCartKey();
      const cartData = {
        items: state.items,
        subtotal: state.subtotal,
        itemsCount: state.itemsCount,
        timestamp: Date.now()
      };
      await AsyncStorage.setItem(cartKey, JSON.stringify(cartData));
      console.log('🛒 PersistentCartContext: Carrito guardado para:', cartKey);
    } catch (error) {
      console.error('❌ Error guardando carrito localmente:', error);
    }
  };

  const loadCartLocally = async (): Promise<PersistentCartItem[]> => {
    try {
      const cartKey = getCartKey();
      const cartData = await AsyncStorage.getItem(cartKey);
      if (cartData) {
        const parsed = JSON.parse(cartData);
        console.log('🛒 PersistentCartContext: Carrito local cargado para:', cartKey, parsed.items.length, 'items');
        return parsed.items || [];
      }
    } catch (error) {
      console.error('❌ Error cargando carrito local:', error);
    }
    return [];
  };

  const loadCartFromStorage = async () => {
    try {
      const cartKey = getCartKey();
      const cartData = await AsyncStorage.getItem(cartKey);
      if (cartData) {
        const parsed = JSON.parse(cartData);
        const items = parsed.items || [];
        const subtotal = items.reduce((sum: number, item: PersistentCartItem) => sum + item.subtotal, 0);
        const itemsCount = items.reduce((sum: number, item: PersistentCartItem) => sum + item.cantidad, 0);
        
        return { items, subtotal, itemsCount };
      }
    } catch (error) {
      console.error('❌ Error cargando carrito desde storage:', error);
    }
    return null;
  };

  const loadCart = async () => {
    try {
      dispatch({ type: 'LOADING' });
      
      // Primero cargar desde AsyncStorage (más rápido)
      const localCart = await loadCartFromStorage();
      if (localCart && localCart.items.length > 0) {
        console.log('🛒 Carrito local cargado primero:', localCart);
        dispatch({
          type: 'LOAD_CART',
          payload: localCart
        });
      }
      
      // Luego intentar sincronizar con el servidor (en segundo plano)
      try {
        const serverCart = await APIService.getCart();
        console.log('🛒 Carrito del servidor cargado:', serverCart);
        
        dispatch({
          type: 'LOAD_CART',
          payload: {
            items: serverCart.items || [],
            subtotal: serverCart.subtotal || 0,
            itemsCount: serverCart.items_count || 0
          }
        });
        
        dispatch({ type: 'SET_ONLINE', payload: true });
      } catch (serverError) {
        console.warn('⚠️ No se pudo cargar carrito del servidor, usando caché local');
        // No hacer nada - ya cargamos el local primero
        dispatch({ type: 'SET_ONLINE', payload: false });
      }
    } catch (error) {
      console.error('❌ Error cargando carrito:', error);
      dispatch({ type: 'STOP_LOADING' });
    }
  };

  const addToCart = async (
    product: { id: number; nombre: string; precio: number; imagen?: string; stock?: number }, 
    quantity: number = 1,
    notas?: string
  ) => {
    try {
      dispatch({ type: 'LOADING' });
      
      const cartItem: PersistentCartItem = {
        id_producto: product.id,
        producto_nombre: product.nombre,
        cantidad: quantity,
        precio_unitario: product.precio,
        subtotal: product.precio * quantity,
        imagen: product.imagen,
        stock: product.stock,
        notas
      };

      // Agregar localmente primero
      dispatch({ type: 'ADD_ITEM', payload: cartItem });
      
      // Intentar sincronizar con servidor
      if (state.isOnline) {
        try {
          await APIService.addToCart(product.id, quantity, notas);
          console.log('✅ Producto agregado al servidor');
        } catch (error) {
          console.warn('⚠️ No se pudo sincronizar con servidor:', error);
          dispatch({ type: 'SET_ONLINE', payload: false });
        }
      }
      
    } catch (error) {
      console.error('❌ Error agregando al carrito:', error);
      dispatch({ type: 'STOP_LOADING' });
      throw error;
    }
  };

  const updateQuantity = async (productId: number, quantity: number) => {
    try {
      console.log(`🔢 Actualizando cantidad: Producto ${productId}, Nueva cantidad: ${quantity}`);
      dispatch({ type: 'LOADING' });
      
      // Actualizar localmente
      dispatch({ type: 'UPDATE_ITEM', payload: { id_producto: productId, cantidad: quantity } });
      
      // Guardar en AsyncStorage
      const cartKey = getCartKey();
      const updatedItems = state.items.map(item =>
        item.id_producto === productId
          ? { ...item, cantidad: quantity, subtotal: item.precio_unitario * quantity }
          : item
      );
      await AsyncStorage.setItem(cartKey, JSON.stringify(updatedItems));
      console.log('✅ Cantidad actualizada en AsyncStorage');
      
      // Sincronizar con servidor si está online
      if (state.isOnline && user?.id) {
        try {
          // Encontrar el ID del carrito en el servidor
          const item = state.items.find(i => i.id_producto === productId);
          if (item?.id) {
            await APIService.updateCartQuantity(item.id, quantity);
            console.log('✅ Cantidad actualizada en servidor');
          } else {
            console.warn('⚠️ Item no tiene ID de servidor, saltando sincronización');
          }
        } catch (error) {
          console.warn('⚠️ No se pudo sincronizar actualización:', error);
        }
      }
      
      dispatch({ type: 'STOP_LOADING' });
    } catch (error) {
      console.error('❌ Error actualizando cantidad:', error);
      dispatch({ type: 'STOP_LOADING' });
      throw error;
    }
  };

  const removeFromCart = async (productId: number) => {
    try {
      console.log(`🗑️ Eliminando producto del carrito: ${productId}`);
      dispatch({ type: 'LOADING' });
      
      // Encontrar item antes de eliminarlo (para sincronizar con servidor)
      const itemToRemove = state.items.find(i => i.id_producto === productId);
      
      // Remover localmente
      dispatch({ type: 'REMOVE_ITEM', payload: productId });
      
      // Guardar en AsyncStorage
      const cartKey = getCartKey();
      const updatedItems = state.items.filter(item => item.id_producto !== productId);
      await AsyncStorage.setItem(cartKey, JSON.stringify(updatedItems));
      console.log('✅ Producto eliminado de AsyncStorage');
      
      // Sincronizar con servidor si está online
      if (state.isOnline && user?.id) {
        try {
          if (itemToRemove?.id) {
            await APIService.removeFromCart(itemToRemove.id);
            console.log('✅ Producto eliminado del servidor');
          } else {
            console.warn('⚠️ Item no tiene ID de servidor, saltando sincronización');
          }
        } catch (error) {
          console.warn('⚠️ No se pudo sincronizar eliminación:', error);
        }
      }
      
      dispatch({ type: 'STOP_LOADING' });
    } catch (error) {
      console.error('❌ Error removiendo del carrito:', error);
      dispatch({ type: 'STOP_LOADING' });
      throw error;
    }
  };

  const clearCart = async () => {
    try {
      console.log('🗑️ Iniciando limpieza completa del carrito...');
      dispatch({ type: 'LOADING' });
      
      // 1. Limpiar localmente primero
      dispatch({ type: 'CLEAR_CART' });
      console.log('✅ Carrito limpiado localmente en estado');
      
      // 2. Limpiar AsyncStorage con la key correcta
      const cartKey = getCartKey();
      await AsyncStorage.removeItem(cartKey);
      console.log(`✅ Carrito limpiado de AsyncStorage (${cartKey})`);
      
      // 3. Limpiar en servidor si hay usuario autenticado
      if (user?.id) {
        try {
          console.log('🌐 Intentando limpiar carrito en servidor...');
          await APIService.clearCart();
          console.log('✅ Carrito limpiado en servidor');
        } catch (error) {
          console.warn('⚠️ No se pudo limpiar carrito en servidor:', error);
          // No lanzar error, continuar con limpieza local exitosa
        }
      }
      
      console.log('✅ Carrito completamente limpiado exitosamente');
      dispatch({ type: 'STOP_LOADING' });
      
    } catch (error) {
      console.error('❌ Error crítico limpiando carrito:', error);
      dispatch({ type: 'STOP_LOADING' });
      throw error;
    }
  };

  const syncCart = async () => {
    return rateLimiter.executeSync(async () => {
      dispatch({ type: 'LOADING' });
      
      try {
        // Obtener carrito local
        const localItems = state.items.map(item => ({
          id_producto: item.id_producto,
          cantidad: item.cantidad
        }));
        
        if (localItems.length === 0) {
          // Si no hay items locales, solo cargar del servidor
          await loadCart();
          return;
        }
        
        console.log('🛒 Iniciando sincronización del carrito...');
        
        // Sincronizar con servidor
        const syncResult = await APIService.syncCart(localItems);
        
        dispatch({
          type: 'SYNC_SUCCESS',
          payload: {
            items: syncResult.carrito_actualizado.items || [],
            subtotal: syncResult.carrito_actualizado.subtotal || 0,
            itemsCount: syncResult.carrito_actualizado.items_count || 0
          }
        });
        
        dispatch({ type: 'SET_ONLINE', payload: true });
        console.log('✅ Carrito sincronizado exitosamente');
        
      } catch (error) {
        console.error('❌ Error sincronizando carrito:', error);
        dispatch({ type: 'SET_ONLINE', payload: false });
        dispatch({ type: 'STOP_LOADING' });
        throw error;
      }
    });
  };

  return (
    <PersistentCartContext.Provider
      value={{
        state,
        addToCart,
        updateQuantity,
        removeFromCart,
        clearCart,
        loadCart,
        syncCart
      }}
    >
      {children}
    </PersistentCartContext.Provider>
  );
};

// Hook personalizado
export const usePersistentCart = () => {
  const context = useContext(PersistentCartContext);
  if (context === undefined) {
    throw new Error('usePersistentCart must be used within a PersistentCartProvider');
  }
  return context;
};
